import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { saveDraft, loadDraftEntry, clearDraft, userDraftName } from "util/guestDraft";
import { showToast } from "util/appToast";
import { useAuth } from "context/AuthContext";
import ConfirmModal from "components/ui/ConfirmModal";

export const DRAFT_SAVED_TEXT = "임시저장됐어요.";
export const DRAFT_RESTORED_TEXT = "임시저장한 내용을 불러왔어요.";
export const DRAFT_FAILED_TEXT = "임시저장에 실패했어요.";
export const DRAFT_RESTORE_ASK_TITLE = "임시저장한 내용이 있어요. 불러올까요?";
export const DRAFT_RESTORE_LOAD_TEXT = "불러오기";
export const DRAFT_RESTORE_FRESH_TEXT = "새로 시작";

/** 저장 시각 표시: 'M월 d일 HH:mm'. */
export function formatDraftSavedAt(ts) {
  const d = new Date(ts);
  if (!ts || Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}월 ${d.getDate()}일 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const draftRestoreAskBody = (savedAt) => {
  const at = formatDraftSavedAt(savedAt);
  return `불러오면 지금 저장된 내용 대신 임시저장한 내용으로 바뀌어요.${at ? ` (임시저장: ${at})` : ""}`;
};

/**
 * 수정 화면 키: 서버에 이미 저장된 내용이 있어 초안을 바로 덮지 않고 먼저 묻는다.
 *  '*:edit:<id>'(공고·대타·레슨·글·이력서 수정), 'resume:profile'(강사·구직 프로필), 'profile'(내 정보).
 */
export const isEditDraftKey = (key) => typeof key === "string"
  && (key.includes(":edit:") || key === "resume:profile" || key === "profile");

// ── 불러오기 확인창(수정 화면). 모달은 DefaultLayout 의 <DraftRestoreHost/> 하나가 그린다 ──
let askState = null; // { owner, savedAt, onLoad, onFresh }
const askListeners = new Set();
const setAsk = (next) => { askState = next; askListeners.forEach((l) => l()); };
const subscribeAsk = (l) => { askListeners.add(l); return () => askListeners.delete(l); };
const getAsk = () => askState;

export function DraftRestoreHost() {
  const ask = useSyncExternalStore(subscribeAsk, getAsk, getAsk);
  return (
    <ConfirmModal
      isShow={Boolean(ask)}
      title={DRAFT_RESTORE_ASK_TITLE}
      body={ask ? draftRestoreAskBody(ask.savedAt) : ""}
      cancelText={DRAFT_RESTORE_FRESH_TEXT}
      confirmText={DRAFT_RESTORE_LOAD_TEXT}
      onCancel={() => ask?.onFresh()}
      onConfirm={() => ask?.onLoad()}
    />
  );
}

// 로그인 회원 키를 읽는다. 회원 키가 비어 있으면 같은 이름의 회원 번호 없는 초안
//  (가입 직전 비회원 초안, 또는 회원별로 나누기 전에 저장한 초안)을 회원 키로 한 번 옮긴다.
function takeEntry(key, userId) {
  if (userId == null) return loadDraftEntry(key);
  const own = userDraftName(userId, key);
  const entry = loadDraftEntry(own);
  if (entry) return entry;
  const plain = loadDraftEntry(key);
  if (!plain) return null;
  saveDraft(own, plain.data, plain.savedAt);
  clearDraft(key);
  return plain;
}

/**
 * 작성 화면 공용 임시저장(로컬, util/guestDraft 7일 보관).
 *
 *   const draft = useWriteDraft(isEdit ? `job:edit:${id}` : "job", { form, images }, {
 *     enabled: !done && loaded,     // 버튼·3지선다를 보일 때(완료·로딩·권한 화면에서는 false)
 *     ready: loaded,                // 불러오기 시점(수정 화면은 서버 데이터를 채운 뒤)
 *     onRestore: (d) => { ...폼 채우기 },
 *   });
 *   useHeaderAction(draft.action)              // 상단바 오른쪽 '임시저장'
 *   useUnsavedGuard(value, !done, draft.guard) // 나갈 때 '작성 중인 내용을 저장할까요?'
 *   제출 성공 → draft.clear()   (실패하면 지우지 않는다)
 *
 * - 저장 위치: 로그인 회원은 'u<회원번호>:<key>', 비회원은 '<key>' 그대로(가입 후 복원 흐름).
 *   로그인 회원 키가 비어 있으면 같은 key 의 비회원·예전 초안을 한 번 옮겨 온다.
 * - data 는 직렬화되는 평범한 값만(문자열·배열·객체). 사진은 업로드가 끝난 URL 문자열만 넣는다.
 * - 새로 작성하는 화면: 불러오기는 key 마다 한 번, 바로 불러오고 토스트 '임시저장한 내용을 불러왔어요.'
 *   restored=true 로 가드가 작성 중으로 본다(그냥 나가도 묻는다).
 * - 수정 화면(isEditDraftKey, 또는 confirmRestore: true): 바로 덮지 않고 확인창
 *   [새로 시작](초안 삭제) / [불러오기] 을 띄운다. 임시저장 시각을 함께 보여 준다.
 * - quiet: 불러오되 토스트·작성 중 표시를 하지 않는다(가입 후 자동 등록처럼 화면이 곧 바뀌는 경우).
 */
export default function useWriteDraft(key, data, {
  enabled = true, ready = true, onRestore, quiet = false, confirmRestore,
} = {}) {
  const auth = useAuth();
  const userId = auth?.isLoggedIn ? (auth?.user?.id ?? null) : null;
  const storeKey = key ? (userId != null ? userDraftName(userId, key) : key) : null;

  const dataRef = useRef(data);
  dataRef.current = data;
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const quietRef = useRef(quiet);
  quietRef.current = quiet;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  const askEdit = confirmRestore ?? isEditDraftKey(key);
  const askEditRef = useRef(askEdit);
  askEditRef.current = askEdit;
  const restoredKeyRef = useRef(null);
  const ownerRef = useRef(null);
  if (!ownerRef.current) ownerRef.current = {};
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    if (!key || !ready || restoredKeyRef.current === key) return;
    restoredKeyRef.current = key;
    const uid = userIdRef.current;
    const entry = takeEntry(key, uid);
    const d = entry?.data;
    if (!d || typeof d !== "object") return;
    const apply = () => {
      if (onRestoreRef.current?.(d) === false) return; // 화면이 쓸 수 없는 초안이라고 판단
      if (quietRef.current) return;
      setRestored(true);
      showToast(DRAFT_RESTORED_TEXT);
    };
    if (!askEditRef.current) { apply(); return; }
    const owner = ownerRef.current;
    const close = () => { if (askState?.owner === owner) setAsk(null); };
    setAsk({
      owner,
      savedAt: entry.savedAt,
      onLoad: () => { close(); apply(); },
      onFresh: () => { close(); clearDraft(uid != null ? userDraftName(uid, key) : key); },
    });
  }, [key, ready]);

  // 화면을 떠나면 그 화면이 띄운 확인창도 닫는다.
  useEffect(() => () => {
    if (askState?.owner === ownerRef.current) setAsk(null);
  }, []);

  // silent: 토스트 없이 저장만(화면이 자체 안내를 띄우는 경우).
  const save = useCallback(({ silent = false } = {}) => {
    const ok = saveDraft(storeKey, dataRef.current);
    if (!silent) showToast(ok ? DRAFT_SAVED_TEXT : DRAFT_FAILED_TEXT);
    return ok;
  }, [storeKey]);

  // 회원이면 회원 키와 함께 남아 있을 수 있는 같은 이름의 비회원 초안도 지운다.
  const clear = useCallback(() => {
    if (!storeKey) return;
    clearDraft(storeKey);
    if (storeKey !== key) clearDraft(key);
  }, [storeKey, key]);

  const on = Boolean(key) && enabled;
  const action = useMemo(() => (on ? { label: "임시저장", onClick: () => save() } : null), [on, save]);
  const guard = useMemo(() => (on
    ? { onSaveDraft: () => save(), onDiscardDraft: clear, restored }
    : { restored }), [on, save, clear, restored]);

  return { save, clear, restored, action, guard };
}
